import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Tag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cPath } from "../../constants";

const CardLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const Card = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius["2xl"] || theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 0;
  box-shadow: ${({ theme }) => theme.shadow.cardSm || theme.shadow.cardMd};
  display: flex;
  flex-direction: column;
  width: 200px;
  height: 200px;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadow.cardMd};
    border-color: hsl(${({ theme }) => theme.colors.primary} / 0.25);
  }
  @media (max-width: 768px) {
    width: calc((100% - 10px) / 2);
    min-width: 0;
    height: 160px;
  }
`;

const ImageWrap = styled.div`
  position: relative;
  width: 100%;
  height: 120px;
  background: hsl(${({ theme }) => theme.colors.muted});
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  @media (max-width: 768px) {
    height: 90px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.15s ease;
  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const Content = styled.div`
  text-align: center;
  padding: 0.5rem 0.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 80px;
`;

const Name = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: hsl(${({ theme }) => theme.colors.foreground});
  margin: 0 0 0.5rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  margin-top: auto;
`;

export default function CategoryCard({ category }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const count = Number(category?.couponCount || 0);
  const couponLabel =
    count === 1
      ? `1 ${t("category.couponSingular", "coupon")}`
      : `${count} ${t("category.couponPlural", "coupons")}`;
  const href = cPath(`category/${category.slug}`);

  const handleClick = (e) => {
    e.preventDefault();
    navigate(href);
  };

  return (
    <CardLink href={href} onClick={handleClick}>
      <Card
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(href);
          }
        }}
      >
        <ImageWrap>
          {category.image ? (
            <Image src={category.image} alt={category.name} loading="lazy" />
          ) : (
            <Placeholder>
              <Tag size={28} strokeWidth={1.5} />
            </Placeholder>
          )}
        </ImageWrap>
        <Content>
          <Name>{category.name}</Name>
          <Meta>
            <Tag size={12} />
            <span>{couponLabel}</span>
          </Meta>
        </Content>
      </Card>
    </CardLink>
  );
}
